import React, { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UploadCloud, Trash2, Sparkles, CheckCircle2, Package, Truck, Warehouse, AlertTriangle, FileImage } from "lucide-react";

export default function ScaricoMaterialiPage() {
  const [files, setFiles] = useState([]); // [{file, previewUrl}]
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [technicians, setTechnicians] = useState([]);
  const [technicianId, setTechnicianId] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/users", { params: { role: "tecnico,subappalto" } });
        setTechnicians(r.data || []);
      } catch { /* no-op */ }
    })();
  }, []);

  const addFiles = (list) => {
    const arr = Array.from(list || []).filter((f) => f.type.startsWith("image/"));
    const next = arr.map((f) => ({ file: f, previewUrl: URL.createObjectURL(f) }));
    setFiles((prev) => [...prev, ...next].slice(0, 40));
  };

  const onPick = (e) => addFiles(e.target.files);
  const onDrop = (e) => {
    e.preventDefault();
    addFiles(e.dataTransfer.files);
  };
  const removeAt = (idx) => {
    setFiles((prev) => {
      const copy = [...prev];
      try { URL.revokeObjectURL(copy[idx]?.previewUrl); } catch {}
      copy.splice(idx, 1);
      return copy;
    });
  };
  const clearAll = () => {
    files.forEach((f) => { try { URL.revokeObjectURL(f.previewUrl); } catch {} });
    setFiles([]);
    setResult(null);
  };

  const process = async () => {
    if (!files.length) {
      toast.error("Aggiungi almeno una foto");
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f.file, f.file.name));
      if (technicianId) fd.append("technician_id", technicianId);
      const r = await api.post("/scarico-materiali/bulk-upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
      });
      setResult(r.data);
      const c = r.data?.counters || {};
      toast.success(
        `${c.serials_detected || 0} seriali: ${c.installed_from_traveling || 0} viaggiante, ${c.installed_from_warehouse || 0} generale, ${c.created_new || 0} creati`
      );
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore elaborazione AI");
    } finally {
      setBusy(false);
    }
  };

  const c = result?.counters || {};

  return (
    <div className="space-y-6" data-testid="scarico-materiali-page">
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Magazzino</div>
        <h1 className="mt-1 text-3xl font-bold text-slate-900 lg:text-4xl">Scarico Materiali</h1>
        <p className="mt-2 max-w-3xl text-sm text-slate-600">
          Carica una o più foto dei barcode degli apparati. L'AI estrae automaticamente solo i codici
          che iniziano con il simbolo <code className="rounded bg-slate-100 px-1 py-0.5 font-mono text-xs">%</code>
          {" "}e li porta in stato <b>installato</b>. Se il seriale non è presente in nessun magazzino, viene
          creato come <b>Modem/ONT</b> e marcato comunque come installato.
        </p>
      </div>

      {/* Tecnico installatore (opzionale) */}
      <Card className="rounded-sm border border-slate-200 bg-white p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[260px]">
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
              Tecnico installatore <span className="font-normal normal-case text-slate-400">(opzionale)</span>
            </Label>
            <Select value={technicianId || "none"} onValueChange={(v) => setTechnicianId(v === "none" ? "" : v)}>
              <SelectTrigger className="mt-1 w-full rounded-sm" data-testid="scarico-tech-select">
                <SelectValue placeholder="Nessun tecnico" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— Nessun tecnico —</SelectItem>
                {technicians.map((u) => (
                  <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="text-xs text-slate-500 max-w-md">
            Se selezionato, verrà registrato come <b>tecnico che ha installato</b> i seriali scaricati —
            sia su quelli già a magazzino sia su quelli creati automaticamente.
          </div>
        </div>
      </Card>

      {/* Upload zone */}
      <Card
        className="relative overflow-hidden rounded-sm border-2 border-dashed border-slate-300 bg-white p-6 transition-colors hover:border-emerald-400"
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
      >
        <div className="flex flex-col items-center justify-center gap-3 text-center">
          <UploadCloud className="h-10 w-10 text-slate-400" />
          <div className="text-sm text-slate-600">
            Trascina qui le foto oppure{" "}
            <button
              className="font-semibold text-emerald-600 underline underline-offset-2"
              onClick={() => inputRef.current?.click()}
              data-testid="scarico-pick-files"
            >
              seleziona dal dispositivo
            </button>
          </div>
          <div className="text-xs text-slate-500">JPG · PNG · WEBP — Max 40 immagini · 15 MB cad.</div>
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            onChange={onPick}
            className="hidden"
            data-testid="scarico-file-input"
          />
        </div>
      </Card>

      {/* Preview grid */}
      {files.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-sm font-semibold text-slate-700">
              {files.length} immagin{files.length === 1 ? "e" : "i"} pronte
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="rounded-sm" onClick={clearAll} disabled={busy} data-testid="scarico-clear">
                <Trash2 className="mr-2 h-4 w-4" /> Rimuovi tutte
              </Button>
              <Button
                onClick={process}
                disabled={busy}
                className="rounded-sm bg-emerald-600 hover:bg-emerald-700"
                data-testid="scarico-process"
              >
                <Sparkles className="mr-2 h-4 w-4" /> {busy ? "Elaboro con AI…" : "Analizza e scarica"}
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8">
            {files.map((f, idx) => (
              <div key={idx} className="group relative overflow-hidden rounded-sm border border-slate-200 bg-slate-50">
                <img src={f.previewUrl} alt={f.file.name} className="h-24 w-full object-cover" />
                <button
                  onClick={() => removeAt(idx)}
                  disabled={busy}
                  className="absolute right-1 top-1 rounded-full bg-slate-900/70 p-1 text-white opacity-0 transition-opacity group-hover:opacity-100"
                  data-testid={`scarico-remove-${idx}`}
                  title="Rimuovi"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
                <div className="truncate px-1 py-1 text-[10px] text-slate-500">{f.file.name}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Report */}
      {result && (
        <div className="space-y-3" data-testid="scarico-report">
          <div className="text-sm font-semibold uppercase tracking-wider text-slate-500">Report</div>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
            <KpiCard icon={FileImage} label="File analizzati" value={c.files_processed} tint="slate" />
            <KpiCard icon={Truck} label="Installati da viaggiante" value={c.installed_from_traveling} tint="blue" />
            <KpiCard icon={Warehouse} label="Installati da generale" value={c.installed_from_warehouse} tint="emerald" />
            <KpiCard icon={Package} label="Creati nuovi (Modem/ONT)" value={c.created_new} tint="amber" />
            <KpiCard icon={CheckCircle2} label="Già installati" value={c.already_installed} tint="indigo" />
          </div>

          {(result.file_errors?.length || 0) > 0 && (
            <Card className="rounded-sm border border-red-200 bg-red-50 p-4">
              <div className="flex items-center gap-2 text-sm font-semibold text-red-700">
                <AlertTriangle className="h-4 w-4" /> {result.file_errors.length} file con errore
              </div>
              <ul className="mt-2 space-y-1 text-xs text-red-600">
                {result.file_errors.map((e, i) => (
                  <li key={i}>• <b>{e.filename}</b>: {e.error}</li>
                ))}
              </ul>
            </Card>
          )}

          {(result.details?.length || 0) > 0 && (
            <Card className="rounded-sm border border-slate-200 bg-white p-4">
              <div className="mb-2 text-sm font-semibold text-slate-700">
                Dettaglio seriali ({result.details.length})
              </div>
              <div className="max-h-80 overflow-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 border-b border-slate-200 bg-white">
                    <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                      <th className="py-2 pr-3">Seriale</th>
                      <th className="py-2 pr-3">Azione</th>
                      <th className="py-2">Origine</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.details.map((d, i) => (
                      <tr key={i} className="border-b border-slate-100">
                        <td className="py-1.5 pr-3 font-mono text-xs">{d.serial}</td>
                        <td className="py-1.5 pr-3">
                          <ActionBadge action={d.action} />
                        </td>
                        <td className="py-1.5 text-xs text-slate-500">{d.from || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, tint }) {
  const tints = {
    slate: "bg-slate-50 text-slate-700 border-slate-200",
    blue: "bg-blue-50 text-blue-700 border-blue-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    indigo: "bg-indigo-50 text-indigo-700 border-indigo-200",
  };
  return (
    <div className={`rounded-sm border p-3 ${tints[tint] || tints.slate}`}>
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4" />
        <div className="text-[10px] font-bold uppercase tracking-wider">{label}</div>
      </div>
      <div className="mt-1 text-2xl font-bold" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
        {value ?? 0}
      </div>
    </div>
  );
}

function ActionBadge({ action }) {
  const map = {
    installed: { label: "Installato", cls: "bg-emerald-100 text-emerald-700" },
    created_installed: { label: "Creato + Installato", cls: "bg-amber-100 text-amber-700" },
    already_installed: { label: "Già installato", cls: "bg-indigo-100 text-indigo-700" },
  };
  const m = map[action] || { label: action, cls: "bg-slate-100 text-slate-700" };
  return <span className={`rounded-sm px-2 py-0.5 text-xs font-semibold ${m.cls}`}>{m.label}</span>;
}
